"use client"

import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FaCircleExclamation, FaShieldHalved, FaDog, FaFire, FaClock, FaHandshake } from 'react-icons/fa6';

const rules = [
    {
        icon: <FaShieldHalved className="h-6 w-6" />,
        title: "Bezahlung erforderlich",
        content: "Ohne Zahlung kein Parkvorgang! Ohne nachgewiesene Zahlung wird gegen die Parkplatzordnung verstoßen. Dies ahnden wir mit einer Gebühr von 50 € / Tag."
    },
    {
        icon: <FaClock className="h-6 w-6" />,
        title: "Parken & Übernachten",
        content: "Das Parken und Übernachten ist nur mit gültigem Parkschein gestattet. Das Parkgeld wird online entrichtet. Die Kurtaxe wird bei der Bezahlung automatisch mit abgerechnet."
    },
    {
        icon: <FaDog className="h-6 w-6" />,
        title: "Hunde an der Leine",
        content: "Hunde sind an der Leine zu führen! Bitte haltet den Platz sauber."
    },
    {
        icon: <FaFire className="h-6 w-6" />,
        title: "Feuer & Grillen",
        content: "Offene Feuer sind nicht gestattet. Das Zelten und Grillen auf dem Platz ist untersagt."
    },
    {
        icon: <FaClock className="h-6 w-6" />,
        title: "Nachtruhe",
        content: "Lautstarke Feiern sind nicht gestattet. Die Nachtruhe von 22:00 - 8:00 Uhr ist unbedingt einzuhalten."
    },
    {
        icon: <FaCircleExclamation className="h-6 w-6" />,
        title: "Kurtaxe",
        content: "Dieser Stellplatz ist laut Gemeindesatzung kurabgabepflichtig. Die Kurtaxe wird bei der Online-Bezahlung automatisch mit abgerechnet (zusätzlich zum Ticketpreis). Die Gästekarte wird im Anschluss automatisch per E-Mail zugesendet."
    }
];

const Ordnung = () => {
    const { ref, inView } = useInView({
        threshold: 0.1,
        triggerOnce: true
    });

    return (
        <div ref={ref} className='mx-auto max-w-5xl px-4'>
            {/* Rules grid */}
            <div className="mb-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {rules.map((rule, index) => (
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 30 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        className="group relative overflow-hidden rounded-2xl bg-white/90 p-6 shadow-lg shadow-amber-900/10 backdrop-blur-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-xl"
                    >
                        {/* Decorative background */}
                        <div className="absolute -right-4 -top-4 h-20 w-20 rounded-full bg-amber-200/50 transition-all duration-500 group-hover:scale-150" />

                        <div className="relative">
                            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100 text-amber-600 transition-all duration-300 group-hover:scale-110 group-hover:bg-amber-200">
                                {rule.icon}
                            </div>
                            <h3 className="mb-2 font-special text-lg font-bold text-slate-800">
                                {rule.title}
                            </h3>
                            <p className="text-sm leading-relaxed text-slate-600">
                                {rule.content}
                            </p>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Additional info */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="mb-12 overflow-hidden rounded-2xl bg-slate-800/90 p-8 text-white shadow-xl"
            >
                <div className="flex flex-col gap-6 md:flex-row md:items-center md:gap-8">
                    <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-amber-400/20">
                        <FaHandshake className="h-8 w-8 text-amber-400" />
                    </div>
                    <div>
                        <h3 className="mb-2 font-special text-xl font-bold">Wichtiger Hinweis</h3>
                        <p className="text-slate-300">
                            Der Platz ist eine Wiese die bei Trockenheit trocken und bei Regen nass ist! Im Juli und August kann es voll werden.
                            Wenn ihr damit klarkommt, seid ihr bei uns richtig. Unser Team ist sehr freundlich und entspannt.
                            Laut vielen Gästen sind wir das letzte freie Refugium an der deutschen Ostsee-Küste!
                        </p>
                        <p className="mt-4 text-sm text-slate-400">
                            Kontakt bei Schäden: Polizeistation Prerow (038233 70930) oder Notruf (110)
                        </p>
                    </div>
                </div>
            </motion.div>

            {/* Final message */}
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="text-center"
            >
                <p className="mb-6 text-lg text-slate-700">
                    Wir bitten um rücksichtsvolles, ruhiges und gemeinschaftliches Verhalten und wünschen Euch einen angenehmen Aufenthalt!
                </p>
                <span className="inline-block font-handwritten text-5xl font-bold text-amber-600 md:text-6xl">
                    ALOHA!
                </span>
            </motion.div>
        </div>
    );
};

export default Ordnung;
